import { useQuery } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { Baby, ChevronRight, LogOut, Plus } from "lucide-react"
import { useLogoutMutation } from "@/features/auth/api/auth.mutations"
import { AddChildDialog } from "@/features/children/AddChildDialog"
import { sexTint } from "@/features/children/children.config"
import { useSelectedChild } from "@/features/children/useSelectedChild"
import { dashboardQueryOptions } from "@/features/dashboard/api/dashboard.queries"
import { useErrorDialog } from "@/shared/components/ErrorDialog"
import { Button } from "@/shared/components/ui/button"
import { cn } from "@/shared/lib/utils"

export function ProfilePage() {
  const navigate = useNavigate()
  const { showApiError } = useErrorDialog()
  const { children, selectedChild } = useSelectedChild()
  const logout = useLogoutMutation()

  const onLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate({ to: "/login" }),
      onError: (err) => showApiError(err, { title: "Kunde inte logga ut" }),
    })
  }

  return (
    <main className="flex flex-col gap-8 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Profil</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={onLogout}
          isLoading={logout.isPending}
          className="text-muted-foreground"
        >
          <LogOut />
          Logga ut
        </Button>
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-medium text-muted-foreground">Barnprofiler</h2>
        <div className="flex flex-col gap-2">
          {children.map((child) => (
            <Button key={child.id} asChild variant="outline" className="justify-between">
              <Link to="/profil/barn/$childId" params={{ childId: child.id }}>
                <span className="flex items-center gap-2">
                  <Baby className={cn("size-4", sexTint(child.sex))} />
                  {child.name}
                </span>
                <ChevronRight className="size-4 text-muted-foreground" />
              </Link>
            </Button>
          ))}
          <AddChildDialog
            trigger={
              <Button variant="outline" className="justify-start">
                <Plus />
                Lägg till barnprofil
              </Button>
            }
          />
        </div>
      </section>

      {selectedChild && <SourceFooter childId={selectedChild.id} />}
    </main>
  )
}

function SourceFooter({ childId }: { childId: string }) {
  const { data } = useQuery(dashboardQueryOptions(childId))
  if (!data) return null
  return (
    <footer className="text-xs text-muted-foreground">
      Sömnrekommendationer från{" "}
      <a
        href={data.recommendation.sourceUrl}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 hover:text-foreground"
      >
        {data.recommendation.sourceName}
      </a>
      .
    </footer>
  )
}
